
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import VideoGallery from '../components/VideoGallery';
import { newsArticles, getArticlesByCategory } from '../data/newsData';
import { ExternalLink, Play } from 'lucide-react';

const NewsPage = () => {
  const [filter, setFilter] = useState("All");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Get unique categories
  const categories = ["All", ...new Set(newsArticles.map(article => article.category))];

  // Filter articles based on selected category
  const filteredArticles = getArticlesByCategory(filter);

  const handleArticleClick = (article: typeof newsArticles[0]) => {
    if (article.externalUrl) {
      window.open(article.externalUrl, '_blank');
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Elegant Background Overlay */}
      <div 
        className="fixed inset-0 z-0 opacity-20" 
        style={{
          backgroundImage: "url('/lovable-uploads/f2c064ce-cceb-4826-9ff6-2967b477e588.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed"
        }} 
      />
      
      {/* Elegant geometric pattern overlay */}
      <div className="fixed inset-0 z-0 opacity-5">
        <div className="geometric-pattern w-full h-full"></div>
      </div>

      {/* Header Section */}
      <div className={`relative z-10 py-20 ${isLoaded ? 'fade-in-up' : 'opacity-0'}`}>
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-7xl font-display font-bold bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent drop-shadow-2xl mb-6">
            News & Updates
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-xl md:text-2xl text-white font-light max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
            Stay informed about our latest activities and community impact
          </p>
        </div>
      </div>

      <div className="relative z-10 container-custom pb-20">
        {/* Video Section */}
        <div className={`mb-20 ${isLoaded ? 'fade-in-left' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-white to-amber-300 bg-clip-text text-transparent mb-4 drop-shadow-lg">
              Latest Videos
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-amber-400 to-yellow-500 mx-auto rounded-full"></div>
          </div>
          <VideoGallery />
        </div>

        {/* Category Filter */}
        <div className={`mb-12 ${isLoaded ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category, index) => (
                <button 
                  key={category} 
                  onClick={() => setFilter(category)} 
                  className={`px-8 py-4 rounded-xl font-medium transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 ${
                    filter === category 
                      ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-lg shadow-amber-500/25 font-bold" 
                      : "bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 border border-white/30 hover:text-amber-300"
                  }`}
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 ${isLoaded ? '' : 'opacity-0'}`}>
          {filteredArticles.map((article, index) => (
            <div 
              key={article.id} 
              className="stagger-item group bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105"
              style={{ animationDelay: `${0.6 + (0.1 * index)}s` }}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={article.image}
                  alt={article.title}
                  className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {article.videoUrl && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-white shadow-lg">
                      <Play className="w-8 h-8 ml-1" />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-8">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-white bg-black/50 px-4 py-2 rounded-lg font-medium border border-white/20">
                    {article.date}
                  </span>
                  <span className="text-xs bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-2 rounded-full font-semibold shadow-lg">
                    {article.category}
                  </span>
                </div>
                
                <h3 className="text-xl font-display font-bold mb-4 text-white group-hover:text-amber-300 transition-colors duration-300 leading-tight">
                  {article.title}
                </h3>
                
                <p className="mb-6 text-slate-200 line-clamp-3 leading-relaxed">{article.excerpt}</p>
                
                {article.externalUrl ? (
                  <button 
                    onClick={() => handleArticleClick(article)}
                    className="text-amber-300 hover:text-amber-200 flex items-center gap-2 transition-all duration-300 group font-medium"
                  >
                    Read more 
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                ) : (
                  <Link to={`/news/${article.id}`}>
                    <Button variant="text" className="text-amber-300 hover:text-amber-200 group font-medium">
                      Read more 
                      <span className="inline-block ml-1 group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Content Management Section */}
        <div className={`mt-20 bg-gradient-to-r from-black/40 to-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-10 shadow-2xl ${isLoaded ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '1s' }}>
          <h3 className="text-3xl font-display font-bold text-white mb-6 text-center">Content Management</h3>
          <p className="text-slate-200 mb-8 text-center text-lg leading-relaxed max-w-4xl mx-auto">
            To add new articles, images, or videos, simply edit the <code className="bg-black/60 px-3 py-1 rounded-lg text-amber-300 font-mono">src/data/newsData.ts</code> file. 
            Upload your media files to the project and reference them in the news articles array.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/20 p-6 rounded-xl border border-amber-500/30">
              <h4 className="text-white font-display font-bold mb-3 text-xl">📰 Articles</h4>
              <p className="text-slate-200 leading-relaxed">Add new articles by updating the newsArticles array</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 p-6 rounded-xl border border-purple-500/30">
              <h4 className="text-white font-display font-bold mb-3 text-xl">🎥 Videos</h4>
              <p className="text-slate-200 leading-relaxed">Upload videos and add them to the VideoGallery component</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
